import type { PolicySkill } from "../../types/ead-types.ts";
import {
  DEFAULT_EAD_DIAGRAM_GENERATE_PROMPT,
  getEffectiveDiagramStandard,
} from "../ead-diagram-standard.ts";
import { compileEadScriptToMermaid, effectiveMermaidForSkill } from "./ead-script-to-mermaid.ts";

export function displayKeyFor(skill: PolicySkill | null): string {
  const cfg = skill?.configJson;
  const friendly = cfg?.displayKey;
  if (typeof friendly === "string" && friendly.trim()) return friendly.trim();
  return skill?.skillKey || "";
}

export function skillScriptFor(skill: PolicySkill | null): string {
  const cfg = skill?.configJson;
  const fromConfig = cfg?.eadScript ?? cfg?.ead_script;
  if (typeof fromConfig === "string" && fromConfig.trim()) return fromConfig;
  return (skill?.note || "").trim();
}

export function skillDescriptionFor(skill: PolicySkill | null): string {
  const body = (skill?.bodyMarkdown || "").trim();
  if (body) return body;
  return "";
}

export function eadMermaidFor(skill: PolicySkill | null): string {
  return effectiveMermaidForSkill(skill, skillScriptFor(skill));
}

export { compileEadScriptToMermaid, effectiveMermaidForSkill };

export function grammarSummaryFor(skill: PolicySkill | null): string {
  const raw = skill?.configJson?.eadGrammarSummary;
  return typeof raw === "string" ? raw.trim() : "";
}

export function grammarHelpUrlFor(skill: PolicySkill | null): string {
  const raw = skill?.configJson?.eadGrammarHelpUrl;
  return typeof raw === "string" ? raw.trim() : "";
}

export const EAD_SCRIPT_SIMPLICITY_RULES =
  "Keep the diagram simple: use at most 10 total entity nodes ([Entity] plus .[Sub Entity] combined) and at most 12 action nodes (<Action>). Prefer one clear main flow with only the most important steps.";

export { DEFAULT_EAD_DIAGRAM_GENERATE_PROMPT };

export function diagramGeneratePromptFor(skill: PolicySkill | null): string {
  const cfg = skill?.configJson;
  const custom = cfg?.eadDiagramGeneratePrompt ?? cfg?.ead_diagram_generate_prompt;
  if (typeof custom === "string" && custom.trim()) return custom.trim();
  return getEffectiveDiagramStandard();
}

/** Always compile from the current EAD Script; fall back to stored Mermaid only when compile is empty. */
export function mermaidFromCurrentEadScript(
  skill: PolicySkill | null,
  scriptOverride?: string,
): string {
  const script = (scriptOverride ?? skillScriptFor(skill)).trim();
  if (script) {
    const compiled = compileEadScriptToMermaid(script);
    if (compiled) return compiled;
  }
  const cfg = skill?.configJson;
  const stored = cfg?.eadMermaidMap ?? cfg?.ead_mermaid_map;
  return typeof stored === "string" ? stored.trim() : "";
}

export function countEadScriptElements(script: string): {
  entityCount: number;
  subEntityCount: number;
  actionCount: number;
  conditionCount: number;
  totalEntities: number;
} {
  const entities = new Set<string>();
  const subs = new Set<string>();
  const actions = new Set<string>();
  const conditions = new Set<string>();
  let stripped = script;
  for (const match of script.matchAll(/\.\[([^\]]+)\]/g)) {
    subs.add(match[1].trim().toLowerCase());
    stripped = stripped.replace(match[0], " ");
  }
  for (const match of stripped.matchAll(/\[([^\]]+)\]/g)) {
    entities.add(match[1].trim().toLowerCase());
  }
  for (const match of script.matchAll(/<([^>]+)>/g)) {
    actions.add(match[1].trim().toLowerCase());
  }
  for (const match of script.matchAll(/\{([^}]+)\}/g)) {
    conditions.add(match[1].trim().toLowerCase());
  }
  return {
    entityCount: entities.size,
    subEntityCount: subs.size,
    actionCount: actions.size,
    conditionCount: conditions.size,
    totalEntities: entities.size + subs.size,
  };
}

/** Compose AI-Prompt-style report markdown for view-only HTML rendering. */
export function buildSkillReportMarkdown(
  skill: PolicySkill,
  options?: { includeMermaid?: boolean; includeScript?: boolean },
): string {
  const includeMermaid = options?.includeMermaid ?? false;
  const includeScript = options?.includeScript ?? true;
  const parts: string[] = [];
  const description = skillDescriptionFor(skill);
  const script = skillScriptFor(skill);
  const mermaid = eadMermaidFor(skill);
  const grammar = grammarSummaryFor(skill);
  const helpUrl = grammarHelpUrlFor(skill);

  parts.push(`# ${skill.displayName}`);
  parts.push("");
  parts.push(`**EAD Skill Key:** \`${skill.skillKey}\``);
  parts.push("");
  parts.push("---");
  parts.push("");

  if (description) {
    parts.push("## Skill Description");
    parts.push("");
    parts.push(description);
    parts.push("");
  }

  if (includeScript && script) {
    parts.push("## EAD Script");
    parts.push("");
    parts.push("```ead");
    parts.push(script.trimEnd());
    parts.push("```");
    parts.push("");
  }

  if (includeMermaid && mermaid) {
    parts.push("## EAD Graph");
    parts.push("");
    parts.push("```mermaid");
    parts.push(mermaid);
    parts.push("```");
  }

  if (grammar) {
    parts.push("");
    parts.push("---");
    parts.push("");
    parts.push(`*EAD Script Grammar:* ${grammar}${helpUrl ? ` · [View full grammar](${helpUrl})` : ""}`);
  }

  return parts.join("\n").trim();
}

/** Markdown for EAD Script code block only (section heading rendered separately in UI). */
export function buildSkillScriptBlockMarkdown(script: string): string {
  const text = (script || "").trim();
  if (!text) return "";
  return ["```ead", text, "```"].join("\n");
}
